// src/pages/ArtifactList.jsx
import React, { useEffect, useState } from "react";
import api from "../api";
import {
  Box,
  Paper,
  Grid,
  Typography,
  Button,
  IconButton,
  Alert,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useNavigate } from "react-router-dom";

export default function ArtifactList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    try {
      setLoading(true);
      setError("");

      const res = await api.get(`/api/artifacts`);   // 🔥 Relative API
      setItems(res.data);
    } catch (err) {
      console.error(err);
      setError("Failed to load artifacts. Please check WiFi / Server.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this artifact?")) return;

    try {
      await api.delete(`/api/artifacts/${id}`); // 🔥 Relative API
      load();
    } catch (err) {
      console.error(err);
      alert("Delete failed. Please check server / WiFi.");
    }
  };

  if (loading)
    return (
      <Typography sx={{ textAlign: "center", mt: 4 }}>
        Loading artifacts…
      </Typography>
    );

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Artifacts
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
          <Button
            variant="outlined"
            size="small"
            sx={{ ml: 2 }}
            onClick={load}
          >
            Retry
          </Button>
        </Alert>
      )}

      <Grid container spacing={2}>
        {!error &&
          items.map((a) => (
            <Grid item xs={12} sm={6} md={4} key={a._id}>
              <Paper sx={{ p: 2 }}>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Box
                    sx={{
                      width: 120,
                      height: 90,
                      overflow: "hidden",
                      borderRadius: 1,
                      background: "#eee",
                    }}
                  >
                    {a.imageUrl ? (
                      <img
                        src={a.imageUrl.startsWith("/")
                          ? a.imageUrl
                          : `/${a.imageUrl}`}       // 🔥 Works anywhere
                        alt={a.title}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                        onError={(e) => (e.target.src = "/fallback.png")}
                      />
                    ) : null}
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6">{a.title}</Typography>

                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", mb: 1 }}
                    >
                      {a.description?.slice(0, 80)}...
                    </Typography>

                    <Typography variant="caption" display="block">
                      Visitors: {a.visitorCount || 0}
                    </Typography>

                    <Box sx={{ mt: 1 }}>
                      <Button
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => navigate(`/admin/artifacts/${a._id}`)}
                        sx={{ mr: 1 }}
                      >
                        View
                      </Button>

                      <IconButton
                        color="primary"
                        onClick={() =>
                          navigate(`/admin/artifacts/${a._id}/edit`)
                        }
                      >
                        <EditIcon />
                      </IconButton>

                      <IconButton
                        color="error"
                        onClick={() => remove(a._id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}

        {!error && items.length === 0 && (
          <Grid item xs={12}>
            <Typography>No artifacts yet.</Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
